import { useCallback, useMemo } from "react";
import {
  CommentThreadStatus,
  useGetCommentThreadsByEntityIdsQuery,
  useCreateCommentThreadMutation,
  useReplyToThreadMutation,
  useResolveThreadMutation,
  useReopenThreadMutation,
  useAssignThreadMutation,
  useUnassignThreadMutation,
} from "@axio/graphql/generated/graphql";
import type { GetCommentThreadsByEntityIdsQuery, ModernizedActivityEntityType } from "@axio/graphql/generated/graphql";
import type { Comment, CommentThread, CommentsApi, CommentTargetKind } from "../components/CommentsSection";

type GqlThread = NonNullable<GetCommentThreadsByEntityIdsQuery["getCommentThreadsByEntityIds"]["threads"]>[number];

type GqlComment = GqlThread["comments"][number];

function mapThread(t: GqlThread): CommentThread {
  const sorted = [...t.comments].sort((a, b) => new Date(a.created).getTime() - new Date(b.created).getTime());

  const rootGql = sorted[0];
  if (!rootGql) {
    return {
      root: {
        id: t.id,
        authorId: "",
        body: "",
        createdAt: t.created,
        resolved: t.status === CommentThreadStatus.Resolved,
        resolvedById: null,
        assigneeId: t.assignee?.id ?? null,
        targetKind: t.entityType,
        targetId: t.entityId,
      },
      replies: [],
      outdated: t.status === CommentThreadStatus.Outdated,
    };
  }

  const mapComment = (c: GqlComment, isRoot: boolean): Comment => ({
    id: isRoot ? t.id : c.id,
    authorId: c.author.id,
    authorName: c.author.name,
    body: c.text,
    createdAt: c.created,
    resolved: isRoot ? t.status === CommentThreadStatus.Resolved : false,
    resolvedById: null,
    assigneeId: isRoot ? (t.assignee?.id ?? null) : null,
    assigneeName: isRoot ? (t.assignee?.name ?? null) : null,
    targetKind: t.entityType,
    targetId: t.entityId,
  });

  return {
    root: mapComment(rootGql, true),
    replies: sorted.slice(1).map((c) => mapComment(c, false)),
    outdated: t.status === CommentThreadStatus.Outdated,
  };
}

export function useCommentThreads(entityIds: string[]) {
  const { data, loading, refetch } = useGetCommentThreadsByEntityIdsQuery({
    variables: { entityIds },
    skip: entityIds.length === 0,
    fetchPolicy: "cache-and-network",
  });

  const [createThread] = useCreateCommentThreadMutation();
  const [replyToThread] = useReplyToThreadMutation();
  const [resolveThread] = useResolveThreadMutation();
  const [reopenThread] = useReopenThreadMutation();
  const [assignThread] = useAssignThreadMutation();
  const [unassignThread] = useUnassignThreadMutation();

  const threads = useMemo<CommentThread[]>(() => {
    const raw = data?.getCommentThreadsByEntityIds?.threads;
    if (!raw) return [];
    return raw.map(mapThread);
  }, [data]);

  const allThreadsFn = useCallback((): CommentThread[] => threads, [threads]);

  const threadsForFn = useCallback(
    (kind: CommentTargetKind, id: string): CommentThread[] =>
      threads.filter((t) => t.root.targetKind === kind && t.root.targetId === id),
    [threads]
  );

  const addCommentFn = useCallback(
    (kind: CommentTargetKind, id: string, body: string) => {
      const trimmed = body.trim();
      if (!trimmed) return;
      createThread({
        variables: {
          entityId: id,
          entityType: kind as ModernizedActivityEntityType,
          text: trimmed,
          mentionedUserIds: [],
        },
      }).then(() => refetch());
    },
    [createThread, refetch]
  );

  const addReplyFn = useCallback(
    (rootId: string, body: string) => {
      const trimmed = body.trim();
      if (!trimmed) return;
      replyToThread({
        variables: {
          threadId: rootId,
          text: trimmed,
          mentionedUserIds: [],
        },
      }).then(() => refetch());
    },
    [replyToThread, refetch]
  );

  const resolveFn = useCallback(
    (rootId: string) => {
      resolveThread({ variables: { threadId: rootId } })
        .then((result) => {
          if (!result.data?.resolveThread?.success) {
            console.warn("resolveThread returned success=false", { threadId: rootId });
          }
          return refetch();
        })
        .catch((err) => console.error("resolveThread failed", err));
    },
    [resolveThread, refetch]
  );

  const reopenFn = useCallback(
    (rootId: string) => {
      reopenThread({ variables: { threadId: rootId } })
        .then((result) => {
          if (!result.data?.reopenThread?.success) {
            console.warn("reopenThread returned success=false", { threadId: rootId });
          }
          return refetch();
        })
        .catch((err) => console.error("reopenThread failed", err));
    },
    [reopenThread, refetch]
  );

  const assignFn = useCallback(
    (commentId: string, assigneeId: string | null) => {
      if (assigneeId) {
        assignThread({ variables: { threadId: commentId, assigneeId } }).then(() => refetch());
      } else {
        unassignThread({ variables: { threadId: commentId } }).then(() => refetch());
      }
    },
    [assignThread, unassignThread, refetch]
  );

  const countsForFn = useCallback(
    (kind: CommentTargetKind, id: string) => {
      const scoped = threads.filter((t) => t.root.targetKind === kind && t.root.targetId === id);
      return {
        total: scoped.length,
        unresolved: scoped.filter((t) => !t.root.resolved).length,
      };
    },
    [threads]
  );

  const commentsApi = useMemo<CommentsApi>(
    () => ({
      allThreads: allThreadsFn,
      threadsFor: threadsForFn,
      addComment: addCommentFn,
      addReply: addReplyFn,
      resolve: resolveFn,
      reopen: reopenFn,
      assign: assignFn,
      countsFor: countsForFn,
    }),
    [allThreadsFn, threadsForFn, addCommentFn, addReplyFn, resolveFn, reopenFn, assignFn, countsForFn]
  );

  return { commentsApi, threads, loading, refetch };
}
